// Namespaces of a tenant.
import { useState, type FormEvent } from "react";
import type { Namespace, TenantDetail } from "../api/types";
import { useServices } from "../components/context";
import {
  PendingFilter,
  RestoreInstead,
  useGrace,
  type DeleteTarget,
  type PendingRow,
} from "../components/Grace";
import { ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatTime, slugError } from "../lib/format";
import { atLeast } from "../lib/roles";
import { routeHash } from "../lib/router";
import { registryName } from "../lib/snippets";

/**
 * The delete dialog target of a namespace. The owner always types the slug (api.md 2.8). Delete, impact, restore
 * and purge use the `ns_` ID (registry 0.20.0).
 */
export function namespaceDeleteTarget(n: Namespace): DeleteTarget {
  return {
    id: n.id,
    title: `namespace "${n.slug}"`,
    message: "You can only delete a namespace that has no crates and no images. Its grants stop.",
    typedName: n.slug,
    typedLabel: "namespace slug",
    impact: "namespaces",
  };
}

/** "How namespaces work": where a namespace applies, and where it does not. */
export function NamespacesHowTo() {
  return (
    <aside className="howto" aria-labelledby="ns-howto">
      <h3 id="ns-howto">How namespaces work</h3>
      <ul>
        <li>Crates live in a namespace. Each namespace is one Cargo registry.</li>
        <li>
          The registry URL chooses the namespace when you publish. The index URL ends with{" "}
          <code>{"/t/<tenant>/<namespace>/index/"}</code>.
        </li>
        <li>Grants give principals access per namespace.</li>
        <li>Trust configs and principals are tenant-wide. They apply to every namespace in this tenant.</li>
      </ul>
    </aside>
  );
}

export function Namespaces({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const list = useLoad(() => api.listNamespaces(tenant.slug), [tenant.slug]);
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const action = useAction();
  const grace = useGrace<Namespace>({
    tenant,
    collection: "namespaces",
    role: "owner",
    onChange: list.reload,
  });
  const pendingRows: PendingRow[] = grace.items.map((n) => ({
    id: n.id,
    title: n.slug,
    detail: "Its grants are hidden. They come back with the restore.",
    deletedBy: n.deleted_by,
    deletedAt: n.deleted_at,
    purgeAfter: n.purge_after,
    purge: {
      id: n.id,
      title: `namespace "${n.slug}"`,
      cascade: "Its grants are deleted too.",
      confirm: n.slug,
      confirmLabel: "namespace slug",
    },
  }));
  const slugMsg = slug ? slugError(slug, false) : null;
  const consumer = tenant.plan === "consumer";

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (slugMsg) return;
    const ok = await action.run(async () => {
      await api.createNamespace(tenant.slug, description ? { slug, description } : { slug });
    });
    if (ok) {
      setSlug("");
      setDescription("");
      list.reload();
    }
  }

  return (
    <>
      <Section
        title="Namespaces"
        actions={
          grace.isAdmin ? (
            <PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />
          ) : undefined
        }
      >
        <p className="muted">Each namespace is one Cargo registry. The namespace "default" always exists.</p>
        <NamespacesHowTo />
        <ErrorBanner error={list.error ?? grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : (
          <table className="stack data-table">
            <thead>
              <tr>
                <th>Slug</th>
                <th>Cargo registry</th>
                <th>Description</th>
                <th>Created</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((n) => (
                <tr key={n.id}>
                  <td className="mono">{n.slug}</td>
                  <td className="mono" data-label="Cargo registry">
                    {registryName(tenant.slug, n.slug)}
                  </td>
                  {n.description ? (
                    <td data-label="Description">{n.description}</td>
                  ) : (
                    <td className="empty" />
                  )}
                  <td data-label="Created">{formatTime(n.created_at)}</td>
                  <td className="actions" data-label="Actions">
                    <a
                      className="button-link secondary"
                      href={routeHash({ name: "tenant", tenant: tenant.slug, page: "grants", ns: n.slug })}
                      aria-label={`Grants in ${n.slug}`}
                    >
                      Grants
                    </a>
                    <a
                      className="button-link secondary"
                      href={routeHash({ name: "tenant", tenant: tenant.slug, page: "connect", ns: n.slug })}
                      aria-label={`Connect Cargo to ${n.slug}`}
                    >
                      Connect Cargo
                    </a>
                    {atLeast(tenant.role, "owner") && n.slug !== "default" && (
                      <button
                        type="button"
                        className="danger"
                        disabled={action.busy || grace.busy}
                        onClick={() => grace.askDelete(namespaceDeleteTarget(n))}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {atLeast(tenant.role, "admin") && (
        <Section title="Add a namespace">
          {consumer ? (
            <p className="muted">A consumer tenant has only the "default" namespace.</p>
          ) : (
            <form onSubmit={onCreate} className="form">
              <label>
                Slug
                <input
                  required
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  placeholder="releases"
                />
                {slugMsg && <small className="error">{slugMsg}</small>}
              </label>
              <label>
                Description (optional)
                <input value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} />
              </label>
              <ErrorBanner error={action.error} />
              {grace.isAdmin && (
                <RestoreInstead
                  error={action.error}
                  busy={grace.busy}
                  onRestore={(p) => {
                    action.setError(null);
                    grace.restore(p.id, `namespace "${slug}"`);
                  }}
                />
              )}
              <button type="submit" disabled={action.busy || !!slugMsg}>
                Add namespace
              </button>
            </form>
          )}
        </Section>
      )}
      {grace.ui}
    </>
  );
}
